import React from "react";
import SectionHeading from "../landing/SectionHeading";

/**
 * Institutional Trust — composite v2 section.
 * Merges Stewardship + Governance + Service Providers (Cayman platform) into
 * a single 3-column trust block, with the CV5 / Bell Rock named-counterparty
 * strip beneath. Full details live on /disclosures, /responsible-capital and
 * the dedicated platform infrastructure section on subpages.
 */
const COLUMNS = [
  {
    n: "06.01",
    label: "Governance",
    title: "Institutional architecture",
    body:
      "Independent administration, custody arrangements appropriate to strategy, a defined investment committee process and disciplined conflicts management.",
    rows: [
      { l: "IC", v: "Process" },
      { l: "AML / KYC", v: "Discipline" },
      { l: "Reporting", v: "Documented" },
    ],
  },
  {
    n: "06.02",
    label: "Stewardship",
    title: "Underwriting input — not posture",
    body:
      "Environmental, social and governance factors integrated into diligence, structuring and ongoing engagement. Preference for documented improvements over declaratory positioning.",
    rows: [
      { l: "Environmental", v: "Diligence" },
      { l: "Workforce", v: "Engagement" },
      { l: "Integrity", v: "Oversight" },
    ],
  },
  {
    n: "06.03",
    label: "Platform Infrastructure",
    title: "Cayman fund architecture",
    body:
      "Institutional Cayman SPC platform via CV5 Capital, with Cayman governance and corporate-services support through Bell Rock Group — subject to final engagement.",
    rows: [
      { l: "Platform", v: "CV5 SPC" },
      { l: "Governance", v: "Bell Rock" },
      { l: "Regulator", v: "CIMA" },
    ],
  },
];

const NAMED_COUNTERPARTIES = [
  {
    name: "CV5 Capital",
    function: "Preferred Fund Platform Provider",
    href: "https://www.cv5capital.io/en",
    label: "cv5capital.io",
  },
  {
    name: "Bell Rock Group",
    function: "Preferred Governance Provider · Cayman",
    href: "https://www.bellrockgroup.com/",
    label: "bellrockgroup.com",
  },
];

const InstitutionalTrust = () => {
  return (
    <section
      id="institutional-trust"
      data-testid="kr-v2-trust"
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)]"
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 items-end mb-16">
          <div className="lg:col-span-8">
            <SectionHeading
              index="06"
              label="Institutional Trust"
              title={
                <>
                  Governance, stewardship and platform infrastructure —{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    designed together
                  </span>
                  .
                </>
              }
            />
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <a
              href="/disclosures"
              data-testid="kr-v2-trust-disclosures"
              className="kr-link font-mono-label text-[11px]"
            >
              Full Disclosures →
            </a>
          </div>
        </div>

        {/* Three-column composite */}
        <div className="grid md:grid-cols-3 gap-0 border-t border-l border-[var(--kr-line)] mb-12">
          {COLUMNS.map((c) => (
            <div
              key={c.n}
              data-testid={`kr-v2-trust-col-${c.n}`}
              className="p-10 lg:p-12 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.025)] transition-colors duration-500"
            >
              <div className="flex items-center gap-3 mb-8">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  {c.n}
                </span>
                <span className="w-8 h-px bg-[var(--kr-gold)]/60" />
                <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                  {c.label}
                </span>
              </div>

              <h3 className="font-serif-display text-[var(--kr-text)] text-[1.5rem] leading-[1.2] mb-5">
                {c.title}
              </h3>
              <p className="text-[var(--kr-text-muted)] text-[14.5px] leading-[1.8] mb-8">
                {c.body}
              </p>

              <div className="border-t border-[var(--kr-line)]">
                {c.rows.map((r) => (
                  <div
                    key={r.l}
                    className="flex items-baseline justify-between py-3.5 border-b border-[var(--kr-line)] last:border-b-0"
                  >
                    <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                      {r.l}
                    </span>
                    <span className="font-serif-display text-[1.1rem] text-[var(--kr-text)]">
                      {r.v}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Named counterparties strip */}
        <div
          data-testid="kr-v2-trust-counterparties"
          className="border border-[var(--kr-gold)]/40 p-7 lg:p-9 grid md:grid-cols-2 gap-8"
          style={{ background: "rgba(201,169,110,0.04)" }}
        >
          {NAMED_COUNTERPARTIES.map((c) => (
            <div key={c.name} className="flex items-center gap-6">
              <span
                aria-hidden
                className="block w-[10px] h-[10px] flex-shrink-0"
                style={{
                  background: "var(--kr-gold)",
                  transform: "rotate(45deg)",
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono-label text-[9.5px] text-[var(--kr-text-muted)] mb-1.5">
                  {c.function}
                </div>
                <div className="font-serif-display text-[var(--kr-text)] text-[1.25rem] leading-[1.15]">
                  {c.name}
                </div>
              </div>
              <a
                href={c.href}
                target="_blank"
                rel="noreferrer"
                className="kr-link font-mono-label text-[10px] hidden sm:block"
              >
                {c.label} ↗
              </a>
            </div>
          ))}
        </div>

        <p className="text-[var(--kr-text-dim)] text-[12px] leading-[1.7] max-w-[110ch] mt-8">
          Service-provider arrangements remain subject to onboarding, conflict
          clearance, engagement letters and final governance sign-off. Names
          should not be presented as formally appointed unless confirmed in
          writing.
        </p>
      </div>
    </section>
  );
};

export default InstitutionalTrust;
